import React, { useEffect, useState, useCallback } from "react";
import { Helmet } from "react-helmet";
import axios from "axios";
import "../css/ip-address.css";

// Icons
import { 
  FaGlobe,
  FaMapMarkerAlt,
  FaCopy,
  FaCheck,
  FaInfoCircle
} from "react-icons/fa";

const IPAddress = () => {
  const [ipData, setIpData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const fetchIPData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Try multiple IP services for better reliability
      let ipInfo = null;
      
      try {
        // Primary service - ipapi.co (provides detailed info)
        const response = await axios.get('https://ipapi.co/json/', {
          timeout: 5000
        });
        
        if (response.data && response.data.ip) {
          ipInfo = {
            ip: response.data.ip,
            city: response.data.city || 'Unknown',
            region: response.data.region || 'Unknown',
            country: response.data.country_name || 'Unknown',
            countryCode: response.data.country_code || '',
            timezone: response.data.timezone || 'Unknown',
            isp: response.data.org || 'Unknown',
            asn: response.data.asn || 'Unknown',
            latitude: response.data.latitude || null,
            longitude: response.data.longitude || null,
            postal: response.data.postal || 'Unknown'
          };
        }
      } catch (primaryError) {
        console.log('Primary IP service failed, trying backup...');
        
        try {
          // Backup service - ipify + ipapi
          const ipResponse = await axios.get('https://api.ipify.org?format=json', {
            timeout: 3000
          });
          
          if (ipResponse.data && ipResponse.data.ip) {
            // Get additional info from ip-api.com
            const detailResponse = await axios.get(`http://ip-api.com/json/${ipResponse.data.ip}`, {
              timeout: 3000
            });
            
            const details = detailResponse.data;
            ipInfo = {
              ip: ipResponse.data.ip,
              city: details.city || 'Unknown',
              region: details.regionName || 'Unknown',
              country: details.country || 'Unknown',
              countryCode: details.countryCode || '',
              timezone: details.timezone || 'Unknown',
              isp: details.isp || 'Unknown',
              asn: details.as || 'Unknown',
              latitude: details.lat || null,
              longitude: details.lon || null,
              postal: details.zip || 'Unknown'
            };
          }
        } catch (backupError) {
          console.log('Backup service also failed, using basic service...');
          
          // Final fallback - just get IP
          const basicResponse = await axios.get('https://api.ipify.org?format=json', {
            timeout: 3000
          });
          
          if (basicResponse.data && basicResponse.data.ip) {
            ipInfo = {
              ip: basicResponse.data.ip,
              city: 'Unknown',
              region: 'Unknown',
              country: 'Unknown',
              countryCode: '',
              timezone: 'Unknown',
              isp: 'Unknown',
              asn: 'Unknown',
              latitude: null,
              longitude: null,
              postal: 'Unknown'
            };
          }
        }
      }

      if (ipInfo) {
        // Add browser and system info
        const browserInfo = getBrowserInfo();
        setIpData({
          ...ipInfo,
          ...browserInfo
        });
      } else {
        throw new Error('Unable to fetch IP information');
      }

    } catch (error) {
      console.error('Error fetching IP data:', error);
      setError('Unable to fetch IP information. Please check your internet connection.');
    } finally {
      setIsLoading(false);
    }
  }, []); // useCallback dependency array

  useEffect(() => {
    fetchIPData();
  }, [fetchIPData]);

  const getBrowserInfo = () => {
    const userAgent = navigator.userAgent;
    const platform = navigator.platform;
    const language = navigator.language;
    
    // Detect browser
    let browser = 'Unknown';
    if (userAgent.includes('Chrome')) browser = 'Chrome';
    else if (userAgent.includes('Firefox')) browser = 'Firefox';
    else if (userAgent.includes('Safari')) browser = 'Safari';
    else if (userAgent.includes('Edge')) browser = 'Edge';
    else if (userAgent.includes('Opera')) browser = 'Opera';

    // Detect OS
    let os = 'Unknown';
    if (userAgent.includes('Windows')) os = 'Windows';
    else if (userAgent.includes('Mac')) os = 'macOS';
    else if (userAgent.includes('Linux')) os = 'Linux';
    else if (userAgent.includes('Android')) os = 'Android';
    else if (userAgent.includes('iOS')) os = 'iOS';

    // Detect device type
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    
    return {
      browser,
      os,
      platform,
      language,
      deviceType: isMobile ? 'Mobile' : 'Desktop',
      userAgent: userAgent.substring(0, 100) + (userAgent.length > 100 ? '...' : '')
    };
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const refreshData = () => {
    fetchIPData();
  };

  return (
    <div className="ip-address-page">
      <Helmet>
        <title>What's My IP Address? - Check Your Public IP | ClubHosty</title>
        <meta
          name="description"
          content="Find out your public IP address instantly. Check your location, ISP, browser info and more with our free IP lookup tool."
        />
        <link rel="canonical" href="https://www.clubhosty.com/ip-address" />
      </Helmet>

      {/* Hero Section - Minimal & Modern Design */}
      <section className="ip-hero-minimal">
        <div className="container">
          <div className="hero-content-minimal">
            <div className="hero-text-section">
              <h1 className="hero-title-minimal">
                Your IP Address
              </h1>
              <p className="hero-desc-minimal">
                Instantly view your public IP address and network details
              </p>
            </div>

            <div className="ip-display-minimal">
              {isLoading ? (
                <div className="ip-loading">
                  <div className="loading-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <p>Detecting...</p>
                </div>
              ) : error ? (
                <div className="ip-error">
                  <div className="error-content">
                    <FaInfoCircle className="error-icon-minimal" />
                    <p>Unable to detect IP address</p>
                    <button onClick={refreshData} className="retry-btn-minimal">
                      Retry
                    </button>
                  </div>
                </div>
              ) : (
                <div className="ip-success">
                  <div className="ip-main-display-minimal">
                    <span className="ip-number">{ipData?.ip}</span>
                    <button 
                      onClick={() => copyToClipboard(ipData?.ip)}
                      className="copy-btn-minimal"
                      title="Copy"
                    >
                      {copied ? <FaCheck /> : <FaCopy />}
                    </button>
                  </div>
                  
                  {ipData?.country && (
                    <div className="ip-location-minimal">
                      <FaMapMarkerAlt className="location-icon" />
                      <span>{ipData.city}, {ipData.country}</span>
                    </div>
                  )}
                  
                  <button 
                    onClick={refreshData} 
                    className="refresh-btn-minimal"
                    disabled={isLoading}
                  >
                    <FaGlobe />
                    Refresh
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {copied && (
          <div className="toast-notification">
            <FaCheck />
            <span>Copied!</span>
          </div>
        )}
      </section>

      {/* Detailed Information - Minimal Cards */}
      {!isLoading && !error && ipData && (
        <section className="ip-details-minimal">
          <div className="container">
            <div className="details-header-minimal">
              <h2>Network Details</h2>
            </div>
            
            <div className="details-grid-minimal">
              {/* Location */}
              <div className="detail-card-minimal">
                <div className="card-label">Location</div>
                <div className="detail-items-minimal">
                  {ipData.countryCode && (
                    <div className="detail-row">
                      <span>Country</span>
                      <span>
                        <span className="flag-minimal">{String.fromCodePoint(...ipData.countryCode.toUpperCase().split('').map(char => 127397 + char.charCodeAt()))}</span>
                        {ipData.country}
                      </span>
                    </div>
                  )}
                  <div className="detail-row">
                    <span>City</span>
                    <span>{ipData.city}</span>
                  </div>
                  <div className="detail-row">
                    <span>Region</span>
                    <span>{ipData.region}</span>
                  </div>
                  <div className="detail-row">
                    <span>Timezone</span>
                    <span>{ipData.timezone}</span>
                  </div>
                </div>
              </div>

              {/* Network */}
              <div className="detail-card-minimal">
                <div className="card-label">Network</div>
                <div className="detail-items-minimal">
                  <div className="detail-row">
                    <span>ISP</span>
                    <span>{ipData.isp}</span>
                  </div>
                  <div className="detail-row">
                    <span>ASN</span>
                    <span>{ipData.asn}</span>
                  </div>
                  {ipData.latitude && (
                    <div className="detail-row">
                      <span>Coordinates</span>
                      <span>{ipData.latitude}, {ipData.longitude}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Device */}
              <div className="detail-card-minimal">
                <div className="card-label">Device</div>
                <div className="detail-items-minimal">
                  <div className="detail-row">
                    <span>Browser</span>
                    <span>{ipData.browser}</span>
                  </div>
                  <div className="detail-row">
                    <span>OS</span>
                    <span>{ipData.os}</span>
                  </div>
                  <div className="detail-row">
                    <span>Type</span>
                    <span>{ipData.deviceType}</span>
                  </div>
                  <div className="detail-row">
                    <span>Language</span>
                    <span>{ipData.language}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Privacy Section - Minimal */}
      <section className="privacy-section-minimal">
        <div className="container">
          <div className="privacy-content-minimal">
            <h2>Privacy Information</h2>
            <div className="privacy-items">
              <div className="privacy-item">
                <div className="privacy-point">
                  <span className="point-number">1</span>
                  <div className="point-content">
                    <h4>What's Visible</h4>
                    <p>Your IP address reveals your approximate location and internet service provider.</p>
                  </div>
                </div>
              </div>
              
              <div className="privacy-item">
                <div className="privacy-point">
                  <span className="point-number">2</span>
                  <div className="point-content">
                    <h4>Privacy Protection</h4>
                    <p>Use a VPN service to mask your real IP address and enhance your privacy.</p>
                  </div>
                </div>
              </div>
              
              <div className="privacy-item">
                <div className="privacy-point">
                  <span className="point-number">3</span>
                  <div className="point-content">
                    <h4>Security Tips</h4>
                    <p>Be cautious when sharing personal information on unsecured networks.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default IPAddress;
